import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { FormActions } from "../../components/FormActions";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { SelectInput } from "../../components/SelectInput";
import { InfoBox } from "../../components/InfoBox";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../../utils/dateUtils";
import { DeviceClassification } from "@KassaroAB/shared/types/enums/DeviceClassification";
import type { RestDevice } from "@KassaroAB/shared/types/device";
import type { DeviceId, StoreId } from "@KassaroAB/shared/types/ids";
import { useClassifyDeviceMutation } from "../../query/devicesQuery";

const OptionRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  margin-bottom: ${spacing.x2}px;
  cursor: pointer;
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const OptionHint = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const getActivatedAt = (device: RestDevice): string =>
  device.appearedOverLimitAt ?? device.createdAt;

const wasLastSeenBeforeActivation = (
  candidate: RestDevice,
  device: RestDevice
): boolean => candidate.lastSeenAt < getActivatedAt(device);

const findReplacementSuggestion = (
  device: RestDevice,
  devices: RestDevice[]
): RestDevice | null => {
  const candidates = devices
    .filter(
      (candidate) =>
        candidate.id !== device.id &&
        candidate.isActive &&
        candidate.deviceModel === device.deviceModel &&
        candidate.platform === device.platform &&
        wasLastSeenBeforeActivation(candidate, device)
    )
    .sort((first, second) => second.lastSeenAt.localeCompare(first.lastSeenAt));

  return candidates[0] ?? null;
};

const getDeviceDisplayName = (
  device: RestDevice,
  unnamedLabel: string
): string => device.name?.trim() || unnamedLabel;

const getDeviceOptionLabel = (
  device: RestDevice,
  unnamedLabel: string,
  lastSeenLabel: string
): string => {
  const parts = [
    getDeviceDisplayName(device, unnamedLabel),
    device.deviceModel,
    device.serialNumber,
  ].filter((part): part is string => Boolean(part && part.trim()));

  return `${parts.join(" · ")} · ${lastSeenLabel} ${formatDate(device.lastSeenAt)}`;
};

interface DeviceClassificationModalProps {
  storeId: StoreId | null;
  device: RestDevice;
  devices: RestDevice[];
  onClose: () => void;
}

export const DeviceClassificationModal = ({
  storeId,
  device,
  devices,
  onClose,
}: DeviceClassificationModalProps) => {
  const { t } = useTranslation();
  const suggestion = findReplacementSuggestion(device, devices);
  const { mutate: classifyDevice, isPending, error } =
    useClassifyDeviceMutation(storeId);
  const form = useForm({
    defaultValues: {
      classification: suggestion
        ? DeviceClassification.REPLACEMENT
        : DeviceClassification.NEW_DEVICE,
      replacedDeviceId: (suggestion?.id ?? "") as DeviceId | "",
    },
    onSubmit: ({ value }) => {
      classifyDevice(
        {
          deviceId: device.id,
          classification: {
            classification: value.classification,
            replacedDeviceId:
              value.classification === DeviceClassification.REPLACEMENT &&
              value.replacedDeviceId
                ? value.replacedDeviceId
                : undefined,
          },
        },
        { onSuccess: onClose }
      );
    },
  });
  const classification = useStore(
    form.store,
    (state) => state.values.classification
  );
  const replacedDeviceId = useStore(
    form.store,
    (state) => state.values.replacedDeviceId
  );

  const unnamedLabel = t("devicesView.unnamedDevice");
  const lastSeenLabel = t("devicesView.tableLastSeen");
  const replacementCandidates = devices
    .filter(
      (candidate) =>
        candidate.id !== device.id &&
        candidate.isActive &&
        wasLastSeenBeforeActivation(candidate, device)
    )
    .sort((first, second) => {
      if (suggestion) {
        if (first.id === suggestion.id) return -1;
        if (second.id === suggestion.id) return 1;
      }
      return second.lastSeenAt.localeCompare(first.lastSeenAt);
    });
  const isReplacement =
    classification === DeviceClassification.REPLACEMENT;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("devicesView.classifyTitle", {
        name: device.name ?? t("devicesView.unnamedDevice"),
      })}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        {suggestion && (
          <InfoBox variant="info">
            {t("devicesView.replacementSuggestion", {
              name: [
                getDeviceDisplayName(suggestion, unnamedLabel),
                suggestion.deviceModel,
                suggestion.serialNumber,
              ]
                .filter((part): part is string => Boolean(part && part.trim()))
                .join(" · "),
              lastSeen: formatDate(suggestion.lastSeenAt),
            })}
          </InfoBox>
        )}

        <form.Field name="classification">
          {(field) => (
            <>
              <OptionRow>
                <input
                  type="radio"
                  name="classification"
                  checked={
                    field.state.value === DeviceClassification.NEW_DEVICE
                  }
                  onChange={() =>
                    field.handleChange(DeviceClassification.NEW_DEVICE)
                  }
                />
                <OptionText>
                  <span>{t("devicesView.classifyNewDevice")}</span>
                  <OptionHint>{t("devicesView.classifyNewDeviceHint")}</OptionHint>
                </OptionText>
              </OptionRow>

              <OptionRow>
                <input
                  type="radio"
                  name="classification"
                  checked={isReplacement}
                  onChange={() =>
                    field.handleChange(DeviceClassification.REPLACEMENT)
                  }
                />
                <OptionText>
                  <span>{t("devicesView.classifyReplacement")}</span>
                  <OptionHint>{t("devicesView.classifyReplacementHint")}</OptionHint>
                </OptionText>
              </OptionRow>
            </>
          )}
        </form.Field>

        {isReplacement && (
          <form.Field
            name="replacedDeviceId"
            validators={{
              onChange: ({ value }) =>
                value === "" ? t("devicesView.selectReplacedDevice") : undefined,
            }}
          >
            {(field) => (
              <FormGroup>
                <FormLabel>{t("devicesView.replacedDevice")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(e.target.value as DeviceId | "")
                  }
                  onBlur={field.handleBlur}
                >
                  <option value="">{t("devicesView.selectReplacedDevice")}</option>
                  {replacementCandidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {getDeviceOptionLabel(
                        candidate,
                        unnamedLabel,
                        lastSeenLabel
                      )}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
        )}

        {error && (
          <InfoBox variant="error">{error.message}</InfoBox>
        )}

        <FormActions>
          <Button
            type="button"
            $color="secondary"
            onClick={onClose}
            disabled={isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={
              isPending ||
              (isReplacement && replacedDeviceId === "")
            }
          >
            {isPending
              ? t("common.saving")
              : t("devicesView.classifyConfirm")}
          </Button>
        </FormActions>
      </Form>
    </Modal>
  );
};
